import { useState, useEffect, memo, useRef } from 'react'
import { createClient } from '@supabase/supabase-js'
import CoorayAppBar, { NavigationButtonType } from '../../shared/components/CoorayAppBar';
import { useParams, useSearchParams } from 'react-router-dom';
import CurationRepository from '../../repository/CurationRepository';
import PlaceRepository from '../../repository/PlaceRepository';
import CurationListFilterBar from '../../shared/components/CurationListFilterBar';
import CurationListItem from '../../shared/components/curationitem/CurationListItem';
import FilterModal from '../../shared/components/filtermodal/FilterModal';
import { useNavigate } from 'react-router-dom';
import { CurationMap } from './CurationMap';
import SortModal from '../../shared/components/sortmodal/SortModal';
import CoorayFooter from '../../shared/components/footer/CoorayFooter';
import { AnalyticsEvent } from '../../shared/analytics/AnalyticsEvent';
import { Helmet } from 'react-helmet';
import { usePlaceDetailsContext } from '../placedetails/PlaceDetailsContext';
import { useCurationContext } from './CurationContext';
import { useCurationPlacesContext } from './CurationPlacesContext';
import { useCurationScrollStateContext } from './CurationScrollStateContext';
import { useCurationMembersContext } from './CurationMembersContext';
import { getPlaces, getUserLocation } from './CurationListUtils';
import { useCurationMapContext } from './CurationMapContext';
import { sendAction, sendPageView } from '../../shared/utils/AnalyticsUtils';
import ZipmapRepository from '../../repository/ZipmapRepository';
import SearchModal from '../../shared/components/searchmodal/SearchModal';
import { createSlug } from '../../shared/utils/URLUtils';
import { applyTheme } from '../../shared/utils/ThemeUtils';

const supabase = createClient('https://twblyxzvdbhlhxxurpno.supabase.co', 'eeyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InR3Ymx5eHp2ZGJobGh4eHVycG5vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjAzNDMzNTMsImV4cCI6MjA3NTkxOTM1M30.cOz8DeHN7L00i3VN2wF5NXs2quw95Eaz3-8IQs4ltgw')

function filtersToFilterOptionsMap(filters) {
  const filterOptionMap = {}
  filters
  .flatMap(
    (filter) => filter.filterOptions
    .map(
      (filterOption) => {
        filterOption.filter = {
          id: filter.id,
          label: filter.label
        }
        return filterOption
      }
    )
  )
  .forEach((filterOption) => {
    filterOptionMap[filterOption.id] = filterOption
  })
  return filterOptionMap
}

export default function CurationMapPage({analytics}) {
  const navigate = useNavigate()
  const topRef = useRef()
  const mapRef = useRef()
  const {_, setPlaceDetails} = usePlaceDetailsContext()
  const {placesData, setPlacesData} = useCurationPlacesContext()
  const { curationMapConfig, setCurationMapConfig } = useCurationMapContext()
  const [appBarDropdownItems, setAppBarDropdownItems] = useState([])
  const {curationData, setCurationData} = useCurationContext()
  const [fullScreenLoadingText, setFullScreenLoadingText] = useState(null)
  const [isFilterBottomSheetOpen, setIsFilterBottomSheetOpen] = useState(false)
  const [isSortBottomSheetOpen, setIsSortBottomSheetOpen] = useState(false)
  const [isSearchBottomSheetOpen, setIsSearchBottomSheetOpen] = useState(false)
  const [isWebsitePaused, setIsWebsitePaused] = useState(false)
  const [isWebsitePreview, setIsWebsitePreview] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const { curationId }= useParams()

  useEffect(() => {

    async function getZipmapDetails(slug) {
      const zipmapRepository = new ZipmapRepository()
      try {
        const zipmapResponse = await zipmapRepository.getZipmapDetails(slug, "recommended")
        console.log(zipmapResponse)
        setCurationData({
          curation: {
            id: zipmapResponse.map.id,
            title: zipmapResponse.map.name,
            displayName: zipmapResponse.map.displayName,
            description: zipmapResponse.map.description,
            customLogo: {
              light: zipmapResponse.map.logo,
              dark: zipmapResponse.map.darkLogo
            },
            coverPhoto: zipmapResponse.map.coverPhoto,
            emoji: zipmapResponse.map.defaultMarker,
            isSortAllowed: zipmapResponse.map.isSortAllowed
          },
          filters: [],
          routes: zipmapResponse.routes
        })

        setPlacesData(
          {
            ...placesData,
            places: zipmapResponse.places.map((place) => {
              return {
                ...place,
                title: place.name,
              }
            }),
            totalPlaceCount: zipmapResponse.places.length,
            isPlaceListSneakPeek: false
          }
        )
        setIsWebsitePreview(zipmapResponse.isPreview === true)
        sendPageView(analytics, AnalyticsEvent.CURATION_MAP_VIEW, {
          collectionId: zipmapResponse.map.id
        })

        if (zipmapResponse.map.primaryColor) {
          applyTheme(zipmapResponse.map.primaryColor);
        }
      } catch (error) {
        console.log(`error ${JSON.stringify(error.response.data)}`)
        if (error.response.data.status == "UNDER_CONSTRUCTION") {
          setIsWebsitePaused(true)
        }
      }
    }

    Promise.all([
      getZipmapDetails(curationId)
    ])
  }, [curationId])

  function handleItemClick(itemId, item) {
    const selectedFilterOptionIds = item.filterOptionIds
    console.log(`selectedFilterOptionIds ${JSON.stringify(selectedFilterOptionIds)}`)
    // curationData.filters.filter((item) => item.filterOptions.map((option) => option.id).includes())
    const placeRelevantOptions = []
    const filtersWithRelevantOptions = []

    setPlaceDetails(
      {
        ...item, filters: filtersWithRelevantOptions, filterOptions: placeRelevantOptions
      }
    )
    // console.log(`map button ref ${getComputedStyle(mapButtonRef.current).display}`)
    const slug = createSlug(item.title, itemId)
    navigate(`/${curationId}/${slug}`)
  }

  function handlePopupItemClick(itemId, item) {
    sendAction(analytics, AnalyticsEvent.MAP_PLACE_ROW_CLICK, AnalyticsEvent.CURATION_MAP_VIEW, {
      collectionId: curationData.curation.id,
      placeId: itemId
    })
    handleItemClick(itemId, item)
  }

  function handlePopupItemNavigateClick(itemId) {
    sendAction(analytics, AnalyticsEvent.MAP_MARKER_GOOGLE_NAVIGATE_CLICK, AnalyticsEvent.CURATION_MAP_VIEW, {
      collectionId: curationData.curation.id,
      placeId: itemId
    })
  }

  async function getPlacesAndSetState(selectedFilters, sortType, coordinates, distance, visits) {
    setFullScreenLoadingText("Loading ...")
    console.log(selectedFilters)
    const filterOptionMap = filtersToFilterOptionsMap(curationData.filters)
    const placeList = await getPlaces(supabase, curationData.curation.id, filterOptionMap, selectedFilters, sortType, coordinates, distance, visits)
    setPlacesData(
      {
        places: placeList,
        selectedSortType: sortType,
        selectedFilterOptions: selectedFilters,
        selectedFilterDistance: distance,
        selectedVisitOptions: visits
      }
    )
    setFullScreenLoadingText(null)
  }

  function onFilterButtonClick() {
    sendAction(analytics, AnalyticsEvent.FILTER_BUTTON_CLICK, AnalyticsEvent.CURATION_MAP_VIEW, {
      collectionId: curationData.curation.id
    })
    setIsFilterBottomSheetOpen(true)
  }

  function onSearchButtonClick() {
    sendAction(analytics, AnalyticsEvent.SEARCH_BUTTON_CLICK, AnalyticsEvent.CURATION_MAP_VIEW, {
      collectionId: curationData.curation.id
    })
    // document.getElementById(`sort-modal`).showModal()
    setIsSearchBottomSheetOpen(true)
  }

  function onFilterModalDismiss() {
    setIsFilterBottomSheetOpen(false)
  }
  
  Array.prototype.filterPlacesByName = function (searchInput) {
    if (!searchInput) return this; // Return all places if search input is empty
  
    const lowerCasedInput = searchInput.toLowerCase();
  
    return this.filter(place => 
      place.title && place.title.toLowerCase().includes(lowerCasedInput)
    );
  }

  async function onApplyFilterClick(filterOptionState, filterDistance, visitFilterState) {
    console.log(`apply filter distance ${filterDistance} ${filterOptionState}`)
    setCurationMapConfig(null)
    const filterOptionStateMap = {}
    filterOptionState.forEach((option) => {
        filterOptionStateMap[option.id] = option.isSelected
    })
    const updatedFilterOptions = placesData.selectedFilterOptions.filter(
      (filterOptionId) => !(filterOptionId in filterOptionStateMap) || filterOptionStateMap[filterOptionId]
    )
    filterOptionState.filter((option) => option.isSelected).forEach((option) => {
      if (!updatedFilterOptions.includes(option.id)) {
        updatedFilterOptions.push(option.id)
      }
    })
    let currentSortType = placesData.selectedSortType
    let coordinates = null

    sendAction(analytics, AnalyticsEvent.FILTER_APPLY_CLICK, AnalyticsEvent.CURATION_MAP_VIEW, {
      collectionId: curationData.curation.id,
      filterDistance: filterDistance
    })

    if (currentSortType == "distance" || filterDistance != null) {
      setFullScreenLoadingText("Fetching location ...")
      coordinates = await getUserLocation()
    }
    setFullScreenLoadingText(null)
    if (coordinates == null && currentSortType == "distance") {
      currentSortType = "recommended"
      // setSelectedSortType("last_updated")
    }

    if (coordinates == null && filterDistance != null) {
      filterDistance = null
      console.log("setting existing distance to null")
    }

    setSearchParams(params => {
      console.log(`existing params ${JSON.stringify(visitFilterState)}`)
      if (updatedFilterOptions.length > 0) {
        params.set("filters", updatedFilterOptions.join());
      } else {
        params.delete("filters")
      }
      if (filterDistance) {
        params.set("filterDistance",filterDistance);
      } else {
        params.delete("filterDistance")
      }
      if (!visitFilterState.visited) {
        params.set("isVisited",false);
      } else {
        params.delete("isVisited")
      }
      if (!visitFilterState.nonVisited) {
        params.set("isNonVisited",false);
      } else {
        params.delete("isNonVisited")
      }
      return params;
    })

    console.log(updatedFilterOptions)
    setPlacesData(
      {
        places: [],
        selectedSortType: currentSortType,
        selectedFilterOptions: updatedFilterOptions,
        selectedFilterDistance: filterDistance,
        selectedVisitOptions: visitFilterState
      }
    )
    getPlacesAndSetState(updatedFilterOptions, currentSortType, coordinates, filterDistance, visitFilterState)
  }

  async function onApplySortTypeClick(sortType) {
    sendAction(analytics, AnalyticsEvent.SORT_OPTION_CLICK, AnalyticsEvent.CURATION_MAP_VIEW, {
      collectionId: curationData.curation.id,
      sortType: sortType
    })
    document.getElementById("sort-modal").close()
    setFullScreenLoadingText("Fetching location ...")
    let coordinates = null
    if (sortType == "distance") {
      coordinates = await getUserLocation()
      if (coordinates == null) {
        sortType = "recommended"
      }
    }
    let filterDistance = placesData.selectedFilterDistance
    if (coordinates == null && placesData.selectedFilterDistance != null) {
      filterDistance = null
    }
    setPlacesData(
      {
        places: [],
        selectedSortType: sortType,
        selectedFilterOptions: placesData.selectedFilterOptions,
        selectedFilterDistance: filterDistance,
        selectedVisitOptions: placesData.selectedVisitOptions,
      }
    )
    setFullScreenLoadingText(null)
    getPlacesAndSetState(placesData.selectedFilterOptions, sortType, coordinates, filterDistance, placesData.selectedVisitOptions)
  }

  async function onApplySearchClick(input) {
    console.log(`on apply search click ${input}`)
    setIsSearchBottomSheetOpen(false)
    setPlacesData({
      ...placesData, 
      selectedSearchInput: input
    })
    setSearchParams(params => {
      if (input == null || input.length == 0) {
        params.delete("searchInput")
      } else {
        params.set("searchInput", input);
      }
      return params;
    })
  }

  function onBackPressed() {
    window.history.back()
  }

  function onToggleToListClick() {
    navigate(`/${curationId}/list`)
  }

  return (
    <div className='h-screen w-screen bg-base-100'>
      <Helmet><title>{curationData.curation.title ? curationData.curation.title : "Loading ..."}</title></Helmet>
      <div className={`flex flex-col h-screen fixed w-screen top-0`}>
      <div ref={topRef} className={`fixed md:relative ease-out ease-in duration-300 z-30 w-full md:block md:top-0`}>
          {isWebsitePreview && <div className='w-full text-xs text-center bg-warning text-warning-content p-2'>This map is in preview mode (not live), only editors can see it.</div>}
          <CoorayAppBar 
            shouldHideTitleInSmall={false}
            shouldHideTitleInBig={false}
            title={curationData.curation.displayName}
            navigationType={isWebsitePaused ? NavigationButtonType.Roambear : NavigationButtonType.CustomLogo}
            rightDropdownItems={appBarDropdownItems}
            customLogo={curationData.curation.customLogo}
            onBackPressed={onBackPressed}
            endItems={[]}
          />
        </div>
        <div className='grid grid-cols-2 flex-1 overflow-y-hidden relative'>
          {
            <div className={`col-span-2 h-full max-h-screen relative order-1 md:order-last bg-base-300 max-w-full overflow-x-hidden`}>
              { placesData.places.length > 0 && <div className='relative w-full h-full'>
                  {/* {
                      curationData.curation.title && <div className={`z-20 md:hidden relative`}>
                        <CurationListFilterBar activeFilterSize={placesData.selectedFilterOptions.length + (placesData.selectedVisitOptions.visited ? 0 : 1) + (placesData.selectedVisitOptions.nonVisited ? 0 : 1) + (placesData.selectedFilterDistance == null ? 0 : 1)} onFilterButtonClick={onFilterButtonClick} onSortButtonClick={onSortButtonClick}/>
                      </div>
                  } */}
                  <CurationMap 
                    isDesktop={false} 
                    ref={mapRef}
                    mapId={curationData.curation.id}
                    mapMarkerEmoji={curationData.curation.emoji != null ? curationData.curation.emoji : "📍"}
                    handlePopupItemClick={handlePopupItemClick}
                    handlePopupItemNavigateClick={handlePopupItemNavigateClick}
                    onToggleBackToListClick={() => { onToggleToListClick() }}
                    analytics={analytics}
                    onFilterButtonClick={onFilterButtonClick}
                    onSearchButtonClick={onSearchButtonClick}
                  />

              </div>
              }
            </div>
          }
        </div>
        <FilterModal 
          isFilterModalOpen={isFilterBottomSheetOpen}
          filters={curationData.filters}
          selectedFilterOptions={placesData.selectedFilterOptions}
          existingDistance={placesData.selectedFilterDistance}
          onApplyClick={onApplyFilterClick}
          onFilterModalDismiss={onFilterModalDismiss}
          visitSelections={placesData.selectedVisitOptions}
        />
        <SortModal 
          isSortModalOpen={isSortBottomSheetOpen}
          selectedSortType={placesData.selectedSortType}
          onSortClick={(type)  => {
            onApplySortTypeClick(type)
          } }
          onSortModalDismiss={() => {
            setIsSortBottomSheetOpen(false)
          }}
        />
        <SearchModal 
          existingSearchQuery={placesData.selectedSearchInput}
          isSearchBottomSheetOpen={isSearchBottomSheetOpen}
          onSearchApplyClick={(query)  => {
            // onApplySortTypeClick(type)
            onApplySearchClick(query)
          } }
          onSearchClearClick={() => {
            onApplySearchClick("")
          }}
          onSearchModalDismiss={() => {
            setIsSearchBottomSheetOpen(false)
          }}
        />

        {fullScreenLoadingText &&
          <div className='w-full h-screen fixed top-0 flex flex-col items-center justify-center backdrop-blur-sm bg-base-100/70 z-50'>
              <div className="loading loading-infinity loading-lg"></div>
              <div className='font-normal text-base-content mt-2'>{ fullScreenLoadingText }</div>
          </div>}

          {isWebsitePaused && <div className='w-full h-full fixed top-0 flex flex-col items-center justify-center z-50'>
              <div className='text-lg font-normal text-base-content bg-base-300 flex-1 w-full flex flex-col items-center justify-center px-4'>🚧 This map is under construction ...</div>
          </div>}
      </div>
    </div>
    )
  }